import {inject, Injectable} from '@angular/core';
import {ErrorMessage} from '../../models/error/ErrorMessage';
import {forkJoin, map, Observable, of, switchMap, tap} from 'rxjs';
import {BaseErrorService} from './base-error.service';
import {TenantService} from '../tenant/tenant.service';
import {WatcherHttpClient} from '../httpClient/WatcherHttpClient';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {ErrorLog} from '../../models/error/ErrorLog';
import {ErrorMessageAPI} from '../../api/models/error/ErrorMessageApi';
import { ErrorPriority } from '../../enums/ErrorPriority';

@Injectable({
    providedIn: 'root'
})
export class ErrorMessageService extends BaseErrorService<ErrorMessage> {
    protected readonly necessaryErrorMessageFields: string[] = [
        "Data",
    ];

    private watcherHttpClient: WatcherHttpClient = inject(WatcherHttpClient);
    private tenantService: TenantService = inject(TenantService);

    public getAmountOfErrorItems(errorFilter: ErrorFilter): Observable<number> {
        return this.watcherHttpClient.get<number>("/message/$count" + this.createODataQueryForAllErrorItems(errorFilter))
    }

    public updateErrorType(errorMessage: ErrorMessage, newPriority: ErrorPriority): Observable<void> {
        return this.watcherHttpClient.patch<void>("/message/" + errorMessage.id, {
            "errorPriority": newPriority
        });
    }

    public getMostRecentErrorItems(maxAmountOfItems: number): Observable<ErrorMessage[]> {
        return this.watcherHttpClient.get<ErrorMessageAPI[]>("/message/summary?take=" + maxAmountOfItems).pipe(
            switchMap((errorMessages: ErrorMessageAPI[]) => errorMessages?.length > 0 ?
                forkJoin(errorMessages.map(errorMessage => this.createErrorMessage(errorMessage))) : of([])
            )
        ); 
    } // TODO paginatie toevoegen aan filter op overview

    public solveError(errorMessage: ErrorMessage): Observable<void> {
        return this.watcherHttpClient.patch<void>("/message/" + errorMessage.id, {
            "isSolved": true
        });
    }

    protected getAllWithFilter(filter: string): Observable<ErrorMessage[]> {
        return this.watcherHttpClient.get<ErrorMessageAPI[]>("/message" + filter).pipe(
            switchMap((errorMessages: ErrorMessageAPI[]) => errorMessages?.length > 0 ?
                forkJoin(errorMessages.map(errorMessage => this.createErrorMessage(errorMessage))) : of([])
            )
        );
    }

    private createErrorMessage(errorMessageAPI: ErrorMessageAPI): Observable<ErrorMessage> {
        return forkJoin({
            tenant: this.tenantService.getTenant(errorMessageAPI.tenantId),
            application: this.tenantService.getApplication(errorMessageAPI.applicationId, errorMessageAPI.tenantId)
        }).pipe(
            map(({tenant, application}) => {
                return new ErrorMessage(
                    errorMessageAPI.id,
                    tenant,
                    application,
                    errorMessageAPI.mostRecentOccurred,
                    errorMessageAPI.occurrenceCount,
                    errorMessageAPI.errorPriority,
                    errorMessageAPI.isSolved,
                    errorMessageAPI.data,
                    errorMessageAPI.errorLogs?.map(errorLogApi => new ErrorLog(
                        errorLogApi.id,
                        errorLogApi.occurred
                    )),
                    errorMessageAPI.solution
                )
            })
        );
    }

    protected override getNecessaryFieldsForErrors(): string[] {
        const necessaryFields: string[] = super.getNecessaryFieldsForErrors();
        necessaryFields.push(...this.necessaryErrorMessageFields);
        return necessaryFields;
    }
}
